import React, { useCallback, useState } from 'react';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import ApiService from '@common/services/ApiService';
import { DefaultModal } from '@common/components/default-modal';
import { DefaultButton } from '@common/components/default-button';
import Autocomplete from '@common/components/autocomplete/Autocomplete';
import { withStyles } from 'tss-react/mui';
import { unitResponsesStyles } from './styles';

const api = new ApiService();

const initialData = {
  approver_user_id: null,
  approverUserLabel: ''
};

const VcmDelegateApproverModal = ({
  classes,
  isOpened,
  onClose,
  onRefresh,
  vcm
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);

  const [data, setData] = useState(initialData);

  const handleApproverAutocompleteChange = (id, value) => {
    setData({ approver_user_id: id, approverUserLabel: value });
  };

  const errorStatus = useCallback(
    (field) => {
      return validationErrors && validationErrors.hasOwnProperty(field);
    },
    [validationErrors]
  );

  const errorHelpers = useCallback(
    (field) => {
      return errorStatus(field) ? validationErrors[field][0] : null;
    },
    [errorStatus, validationErrors]
  );

  const makeFieldProps = useCallback(
    (field, fieldValue, errorField) => {
      return {
        error: errorStatus(errorField || field),
        helperText: errorHelpers(errorField || field),
        name: field,
        value: fieldValue
      };
    },
    [errorStatus, errorHelpers]
  );

  const setErrors = useCallback(
    (json) => {
      if (json.code == 1) {
        setErrorMessage(json.message);
      } else if (json.code == 2) {
        setValidationErrors(json.messages);
      }
    },
    [setErrorMessage, setValidationErrors]
  );

  const handleSubmit = useCallback(async () => {
    setIsLoading(true);

    try {
      const url = `/api/v1/vcms/${vcm.id}/delegate`;
      const { approver_user_id } = data;
      const res = await api.put(url, { vcm: { approver_user_id } });

      if (res.status === 200) {
        handleClose();
        onRefresh();
      } else if (res.status === 422 || res.status === 400) {
        setErrors(res.json);
        setErrorMessage(res.json.message);
      } else {
        console.error('Unknown response', res);
      }
    } catch (error) {
      setErrorMessage(error.message);
    } finally {
      setIsLoading(false);
    }
  }, [data]);

  const handleClose = useCallback(() => {
    setErrorMessage(null);
    setValidationErrors([]);
    setData(initialData);
    onClose();
  }, [setErrorMessage, setValidationErrors, setData, onClose]);

  return (
    <DefaultModal
      isOpened={isOpened}
      isLoading={isLoading}
      onClose={handleClose}
      title="Delegate approver"
      action={
        <DefaultButton
          className={classes.button}
          color="secondary"
          onClick={handleSubmit}
          size="small"
          variant="contained"
        >
          Update
        </DefaultButton>
      }
    >
      <Grid container spacing={2}>
        <Grid size={12}>
          <Typography color="error">{errorMessage}</Typography>
        </Grid>
        <Grid size={12}>
          <Typography>Please select a new approver for this VCM</Typography>
        </Grid>
        <Grid size={12} mb={4}>
          <Autocomplete
            label="Search by given name, family name or employee number"
            name="approver_user_id"
            onChange={handleApproverAutocompleteChange}
            role={['supervisor', 'department_manager', 'division_manager', 'admin', 'institute_admin']}
            value={data.approverUserLabel || ''}
          />
        </Grid>
      </Grid>
    </DefaultModal>
  );
};

export default withStyles(VcmDelegateApproverModal, unitResponsesStyles, {
  withTheme: true
});
